import { any, groupBy, map, mapObjIndexed, merge, pipe } from 'ramda';
import uuid from 'uuid-random';
import { IWedding } from '@bridebook/models/source/models/Weddings.types';
import {
  type IGuest,
  type IGuest_Invitation,
  Rsvp,
} from '@bridebook/models/source/models/Weddings/Guests.types';
import { capitalizeFirstLetter } from '@bridebook/toolbox/src/utils/strings';
import {
  GuestsGroup,
  GuestsGroupsByGroupId,
} from '@bridebook/web/components/guestlist/category-list/category-list';
import {
  DefaultCategories,
  InvitationTypes,
  RsvpStatuses,
  RsvpStatusesUI,
} from '@bridebook/web/lib/guestlist/constants';
import { GuestlistFilterType, IGuestFilter } from '@bridebook/web/lib/guestlist/types';
import { getI18n } from '@bridebook/web/lib/i18n/getI18n';
import {
  getOverviewRsvpResponse,
  getOverviewRsvpStatus,
  getRsvpResponse,
  onlyInvitedEventsById,
  toUIRsvpStatus,
} from '@bridebook/web/lib/rsvp/rsvp-helpers';
import { getNextOrder } from '@bridebook/web/lib/utils';
import { makePossessive } from '@bridebook/web/lib/utils/strings';
import { Events } from '@bridebook/models/source/models/Weddings/Events';
import { IWeddingEvent } from '@bridebook/models/source/models/Weddings/Events.types';

const guestDefault = {
  events: {
    wedding: {
      attending: null,
      invitation: '',
    },
  },
  gender: null,
  head: null,
} as IGuest;

/**
 * Returns Object representing statistics for a given guestlist
 * @method guestDataTotals
 * @param {List} guests List representing all guests in given guestlist
 * @returns {Object} Object of guestlist statistics
 */
export const guestDataTotals = (guests: Array<IGuest>): GuestlistFilterType => {
  const all = guests.length;
  const guestsWithEvents = guests.filter((guest) => guest.events?.wedding);
  const day = guestsWithEvents.filter(
    (guest) => guest.events.wedding.invitation === InvitationTypes.day,
  ).length;
  const evening = guestsWithEvents.filter(
    (guest) => guest.events.wedding.invitation === InvitationTypes.evening,
  ).length;
  const waitList = guestsWithEvents.filter(
    (guest) => guest.events.wedding.invitation === InvitationTypes.waitlist,
  ).length;
  const rsvpAll = guestsWithEvents.filter(
    (guest) =>
      guest.events.wedding.attending === RsvpStatuses[RsvpStatusesUI.yes] ||
      guest.events.wedding.attending === RsvpStatuses[RsvpStatusesUI.no],
  ).length;
  const rsvpYes = guestsWithEvents.filter(
    (guest) => guest.events.wedding.attending === RsvpStatuses[RsvpStatusesUI.yes],
  ).length;
  const rsvpNo = guestsWithEvents.filter(
    (guest) => guest.events.wedding.attending === RsvpStatuses[RsvpStatusesUI.no],
  ).length;
  const rsvpNoReply = guestsWithEvents.filter(
    (guest) => guest.events.wedding.attending === RsvpStatuses[RsvpStatusesUI.noReply],
  ).length;

  return {
    all,
    day,
    evening,
    waitList,
    rsvpAll,
    rsvpYes,
    rsvpNo,
    rsvpNoReply,
  };
};

/**
 * Returns new IGuest based on a default one with some partial data.
 */
export const createGuest = ({
  id,
  name,
  categoryId,
  age,
  gender,
  email,
  phone,
  invitationType,
  order,
}: {
  id?: IGuest['id'];
  name?: IGuest['name'];
  categoryId?: IGuest['category'];
  age?: IGuest['age'];
  gender?: IGuest['gender'];
  email?: Required<IGuest>['contacts']['email'];
  phone?: Required<IGuest>['contacts']['phone'];
  invitationType?: InvitationTypes;
  order?: IGuest['order'];
}): IGuest => {
  const i18n = getI18n();
  const newGuestData: Partial<IGuest> = {
    id: id || uuid(),
    name: name ? capitalizeFirstLetter(name.trim()) : i18n.t('guestlist:unknownGuest'),
    category: categoryId || DefaultCategories.me,
    ...(age && { age }),
    ...(gender && { gender }),
    ...((email || phone) && {
      contacts: {
        ...(email && { email }),
        ...(phone && { phone }),
      },
    }),
    events: {
      wedding: {
        ...guestDefault.events.wedding,
        invitation: invitationType || InvitationTypes.day,
      },
    },
    order: order || getNextOrder(),
  };

  return {
    ...guestDefault,
    ...newGuestData,
  };
};

/**
 * Create a new IGuest object with some data from another provided base guest
 *
 * @param {Object} guest
 * @param {string} name - name of the new guest
 * @returns {Object} new IGuest object
 */
export const cloneGuest = (guest: IGuest, name: string): IGuest => {
  const newGuestData: Partial<IGuest> = {
    id: uuid(),
    name: capitalizeFirstLetter(name.trim()),
    category: guest.category,
    events: { ...guest.events },
    order: getNextOrder(),
  };

  return {
    ...guestDefault,
    ...newGuestData,
  };
};

/**
 * Checks if a given guest has any address details filled in
 * @param {IGuest} guest
 * @returns {Boolean}
 */
export const hasAnyAddressDetails = (guest: IGuest): boolean => {
  const { address } = guest;
  return (
    !!address &&
    (any(Boolean, [address.country, address.city, address.postalCode]) ||
      any(Boolean, address.street || []))
  );
};

/**
 * Checks if a given guest has any contact details filled in
 * @param {IGuest} guest
 * @returns {Boolean}
 */
export const hasAnyContactDetails = (guest: IGuest): boolean => {
  const { contacts } = guest;
  return !!contacts && any(Boolean, Object.values(contacts));
};

/**
 * Returns Object filtered immutable list of guests with filters applied
 * @method filterGuestlist
 * @param events
 * @param {Object} filter object containing all filter values
 * @param {Object} list immutable list of all guests in given guestlist
 * @returns {Object} filtered list
 */
export const filterGuestlist = (
  events: IWeddingEvent[],
  filter: IGuestFilter,
  list: Array<IGuest>,
): Array<IGuest> => {
  let result: Array<IGuest> = list;

  Object.entries(filter).forEach(([filterName, filterValue]) => {
    switch (filterName as keyof IGuestFilter) {
      case 'invitationType':
        result = filterValue
          ? result.filter(
              (guest: IGuest) =>
                guest.invitation?.events[Events.MAIN_EVENT] &&
                guest.events.wedding.invitation === filterValue,
            )
          : result;
        break;
      case 'overviewRsvpStatus':
        result = filterValue
          ? result.filter(
              (guest: IGuest) => getOverviewRsvpStatus(events, guest.invitation) === filterValue,
            )
          : result;
        break;
      case 'eventRsvpStatus':
        result = filterValue
          ? result.filter(
              (guest: IGuest) =>
                toUIRsvpStatus(getRsvpResponse(guest.invitation, filter.eventId || '')) ===
                filterValue,
            )
          : result;
        break;
      case 'overviewRsvpResponse':
        result = filterValue
          ? result.filter(
              (guest: IGuest) => getOverviewRsvpResponse(events, guest.invitation) === filterValue,
            )
          : result;
        break;
      case 'age':
        result = filterValue ? result.filter((guest: IGuest) => guest.age === filterValue) : result;
        break;
      case 'gender':
        result = filterValue
          ? result.filter((guest: IGuest) => guest.gender === filterValue)
          : result;
        break;
      case 'contacts': {
        switch (filterValue as IGuestFilter['contacts']) {
          case 'address': {
            const condition: boolean = !!filterValue;
            result = condition
              ? result.filter((guest: IGuest) => hasAnyAddressDetails(guest))
              : result;
            break;
          }
          case 'phone': {
            const condition: boolean = !!filterValue;
            result = condition
              ? result.filter((guest: IGuest) => guest.contacts && guest.contacts.phone)
              : result;
            break;
          }
          case 'email': {
            const condition: boolean = !!filterValue;
            result = condition
              ? result.filter((guest: IGuest) => guest.contacts && guest.contacts.email)
              : result;
            break;
          }
          case 'emailMissing': {
            const condition: boolean = !!filterValue;
            result = condition
              ? result.filter(
                  (guest: IGuest) => !guest.contacts?.email || guest.contacts.email.trim() === '',
                )
              : result;
            break;
          }
        }
        break;
      }
      case 'search':
        result = filterValue
          ? result.filter(
              (guest: IGuest) =>
                guest.name.toLowerCase().indexOf(String(filterValue).toLowerCase()) > -1,
            )
          : result;
        break;
      case 'eventId':
        result = filterValue
          ? result.filter((guest: IGuest) => guest.invitation?.events[String(filterValue)])
          : result;
        break;
      case 'dietaryRequirements':
        result = filterValue
          ? result.filter((guest: IGuest) => guest.dietaryOptions?.includes(filterValue))
          : result;
        break;
      case 'table':
        result = filterValue
          ? result.filter((guest: IGuest) => guest.table === filterValue)
          : result;
        break;
    }
  });

  return result;
};

/**
 * Given the current element it returns the next one in a loop
 * (returns the first one if given element is the last)
 * @param current - current value
 * @param arr - array of values to loop through
 */
export const selectNextElementInLoop = <T>(current: T, arr: Array<T>): T => {
  try {
    const index = arr.indexOf(current);
    return index === arr.length - 1 ? arr[0] : arr[index + 1];
  } catch (err) {
    return arr[0];
  }
};

/**
 * Returns a guest who is the head of the given group
 *
 * @param groupId {string}
 * @param guests {Array<IGuest>}
 * @return {IGuest}
 */
export const findHeadForGroup = (groupId: string, guests: Array<IGuest>): IGuest =>
  guests.find((guest) => guest.id === groupId) ||
  merge(guestDefault, {
    name: getI18n().t('guestlist:unknownGuest'),
    id: groupId,
  });

/**
 * Returns an object with guests, where keys are groupIds and values are
 * another objects with guests divided to "head" and "plusOnes"
 *
 * @param allGuests
 * @param filteredGuests
 */
export const groupGuestsByGroupId = (
  allGuests: Array<IGuest>,
  filteredGuests: Array<IGuest>,
): GuestsGroupsByGroupId => {
  /**
   * Group guests by their groupId
   *
   * @returns an object where keys are IDs of groups and values are arrays with
   * guests in the corresponding group
   */
  const groupByGroupId = groupBy((guest: IGuest) => guest.head || guest.id);

  /**
   * For each key (which is groupId), transform the value so that
   * the guests are grouped by their type - either group head or plus one
   *
   * @returns an object where keys are IDs of groups and values are GuestsGroup objects
   * with 2 keys: "head" and "plusOnes"
   */
  const groupByType = map(
    groupBy((guest: IGuest): keyof GuestsGroup => (guest.head ? 'plusOnes' : 'head')),
  );

  /**
   * Make sure each group always has a group head - even if one doesn't match filters
   *
   * @param guestGroup
   * @param groupId
   */
  const addHeadToGroup = (
    guestGroup: { head?: Array<IGuest>; plusOnes?: Array<IGuest> },
    groupId: string,
  ): GuestsGroup => {
    const head = guestGroup.head ? guestGroup.head[0] : findHeadForGroup(groupId, allGuests);
    const plusOnes = guestGroup.plusOnes || [];
    return { head, plusOnes };
  };

  return pipe<IGuest[], any, any, GuestsGroupsByGroupId>(
    groupByGroupId,
    groupByType,
    mapObjIndexed(addHeadToGroup),
  )(filteredGuests);
};

interface IGuestCategory {
  id: string;
  value: string;
}

export const getCategoryName = ({
  category,
  partners,
}: {
  category: IGuestCategory;
  partners?: IWedding['partners'];
}): string => {
  const i18n = getI18n();
  if (category.id === DefaultCategories.me) {
    return partners && partners[0]
      ? i18n.t('guestlist:categoryPartnerGuests', {
          name: partners[0],
          possessive: makePossessive(partners[0]),
        })
      : i18n.t('guestlist:categoryMyGuests');
  }
  if (category.id === DefaultCategories.partner) {
    return partners && partners[1]
      ? i18n.t('guestlist:categoryPartnerGuests', {
          name: partners[1],
          possessive: makePossessive(partners[1]),
        })
      : i18n.t('guestlist:categoryMyPartnerGuests');
  }
  return category.value;
};

/**
 * Returns id of the group to which the guest belongs to.
 * @param guest
 */
export const getGroupIdByGuest = (guest: IGuest) => guest.head || guest.id;

/**
 * Return all guests belonging to a group with a given id
 * (both head and plus ones together)
 *
 * @param groupId
 * @param guests
 */
export const getGuestsByGroupId = (groupId: string, guests: Array<IGuest>) =>
  guests.filter((guest) => (guest.head && guest.head === groupId) || guest.id === groupId);

/**
 * Similar as getGuestsByGroupId but returns only Plus Ones for a given group
 * @param args
 */
export const getPlusOnesByGroupId = (...args: Parameters<typeof getGuestsByGroupId>) =>
  getGuestsByGroupId(...args).filter((g) => !!g.head);

/**
 * The write that puts a guest into a group: they point at the lead and take
 * the lead's category. The lead itself never points at anyone (`head: null`),
 * so linking cannot chain groups.
 */
export const toGroupLinkPatch = (guestId: string, lead: IGuest): Partial<IGuest> => ({
  category: lead.category,
  head: guestId !== lead.id ? lead.id : null,
});

/** The write that detaches a guest from their group (their category stays). */
export const toGroupUnlinkPatch = (): Partial<IGuest> => ({ head: null });

/**
 * The per-event RSVP entries a status change amounts to — the guest-list UI
 * reads RSVP from `invitation.events`, so every RSVP write has to land there.
 * The status applies to every event the guest is invited to; a guest invited
 * nowhere gets the main-event invite, since answering an RSVP implies one.
 */
export const toRsvpInvitationEvents = (guest: IGuest, rsvp: Rsvp): IGuest_Invitation['events'] => {
  const invitedIds = Object.keys(onlyInvitedEventsById(guest.invitation?.events ?? {}));
  const eventIds = invitedIds.length ? invitedIds : [Events.MAIN_EVENT];
  return Object.fromEntries(eventIds.map((id) => [id, { rsvp }]));
};

export { splitGuestNames } from '@bridebook/web/lib/guestlist/helpers/guest-names';

/**
 * Create new guests from a given list of names.
 * The first name will be group head.
 * @param names
 * @param invitationType
 * @param categoryId
 * @param orderIndex - optional number for ordering
 * @param eventId - optional event id assigned to guest
 */
export const getNewGuestGroup = ({
  names,
  invitationType,
  categoryId,
  orderIndex = 0,
  inviteToEventId,
}: {
  names: Array<string>;
  invitationType: InvitationTypes;
  categoryId: string;
  orderIndex?: number;
  inviteToEventId?: string;
}) => {
  const mainGuestId = uuid();

  const mainGuest: Partial<IGuest> = {
    id: mainGuestId,
    name: names[0],
    category: categoryId,
    events: {
      wedding: {
        ...guestDefault.events.wedding,
        invitation: invitationType,
      },
    },
    order: getNextOrder(orderIndex),
  };

  if (inviteToEventId) {
    mainGuest.invitation = {
      events: {
        [inviteToEventId]: {
          rsvp: Rsvp.PENDING,
        },
      },
    };
  }

  // Names from the second and up will be main guest's Plus Ones
  const plusOnes = names.slice(1, names.length).map(
    (_name: string, i): Partial<IGuest> => ({
      ...mainGuest,
      id: uuid(),
      name: _name,
      head: mainGuestId,
      order: getNextOrder(i),
    }),
  );

  return [mainGuest, ...plusOnes] as Array<IGuest>;
};

/**
 * Returns an object containing a total number of Plus Ones for every guest group.
 * @param guestsInCategory: Array<IGuest>
 */
export const getPlusOnesCounts = (guestsInCategory: Array<IGuest>): Record<string, number> =>
  guestsInCategory.reduce((acc: Record<string, number>, guest: IGuest) => {
    // If a guest doesn't have a reference to the Head, then
    // the guest is the Head himself (and the only member if his group).
    if (!guest.head) {
      acc[guest.id] = guestsInCategory.filter(
        (g) => g.head && g.head === guest.id && g.id !== guest.id,
      ).length;
    }
    return acc;
  }, {});
